import { IoChevronForwardCircleOutline } from "react-icons/io5";
import { LuPackageSearch } from "react-icons/lu";
import { BiReset } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState } from "react";
import { RxCrossCircled } from "react-icons/rx";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import "../../../../styles.css";
import "../../../../slicer-styles.css";
import { PromoMasterDropdownModel, PromoMasterSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/PromoMasterSlicer.model";
import { RootState } from "../../../../store";
import { promoMasterTabActions } from "../../../../store/slices/promo-master-tab-slice";
import { LONG_TERM_PROMOTION_SUB_TAB_ID, PROMO_MASTER_TAB_ID, SHORT_TERM_PROMOTION_SUB_TAB_ID, TOAST_CONTAINER_ID } from "../../../../shared/constants";
import CheckboxDropdown from "../../../UI/CheckboxDropdown/CheckboxDropdown";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { getDefaultSlicersForForecastEntryApp, getToastOptions } from "../../../../shared/functions";
import Modal from "../../../UI/Modal/Modal";

const PromoMasterSlicers = (props: {
    isGridChanged: boolean;
}) => {
    // States
    const [isSlicerChanged, setIsSlicerChanged] = useState<boolean>(false);
    const [slicerChangeAction, setSlicerChangeAction] = useState<"apply" | "reset">("apply");

    // Variables
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const slicerChangeModalId: string = "promotionMasterSlicerChangeModal";

    // Redux variables
    const dispatchFn = useDispatch();
    const slicers: PromoMasterDropdownModel = useSelector((state: RootState) => state.promoMasterTab.slicers);
    const tempSelectedSlicers: PromoMasterSelectedSlicerModel = useSelector((state: RootState) => state.promoMasterTab.tempSelectedSlicers);

    /**
     * Handler for applying selected slicers.
     * @param {Event} event Apply filters button click event.
     * @returns {void}
     */
    const applySelectedSlicers = (event: any): void => {
        const tempSlicers: any = { ...tempSelectedSlicers };
        const selectedSlicers: any = {
            skus: tempSlicers.skus.includes('All') ? ['All'] : tempSlicers.skus
        };
        dispatchFn(promoMasterTabActions.setSelectedSlicers(selectedSlicers));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [LONG_TERM_PROMOTION_SUB_TAB_ID]: false,
            [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false
        }));
        setIsSlicerChanged(false);

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    /**
     * Handler for resetting selected slicers.
     * @param {Event} event Reset filters button click event.
     * @returns {void}
     */
    const resetSelectedSlicers = (event: any): void => {
        const defaultPromoMasterSlicers: PromoMasterSelectedSlicerModel = getDefaultSlicersForForecastEntryApp(PROMO_MASTER_TAB_ID);

        if (
            tempSelectedSlicers.skus.toSorted().toString() !== defaultPromoMasterSlicers.skus.toSorted().toString()
        ) {
            dispatchFn(promoMasterTabActions.resetSelectedSlicers(null));
            dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                [LONG_TERM_PROMOTION_SUB_TAB_ID]: false,
                [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false
            }));
            setIsSlicerChanged(false);
        } else {
            toast.info("No filters have been changed to reset", {
                ...getToastOptions("info"),
                containerId: TOAST_CONTAINER_ID
            });
        }

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    /**
     * Event handler for dropdown value change
     * @param {string} field Slicer field which was updated
     * @param {string} value Updated slicer value
     */
    const dropdownItemChangeHandler = (field: string, value: any) => {
        dispatchFn(promoMasterTabActions.setTempSelectedSlicers({
            ...tempSelectedSlicers,
            [field]: value
        }));
        setIsSlicerChanged(true);
    };

    /**
     * Handler to close slicer change modal
     */
    const closeSlicerChangeModal = (event: any) => {
        modalCloseBtnRef.current?.click();
    };

    // Slicer Change Modal body
    const slicerChangeModalBody: JSX.Element = (
        <div className="row">
            <div className="col col-12 mb-2">
                {`${(slicerChangeAction === "apply") ? "Applying" : "Resetting"} filters will discard the changes done to the grid. Do you want to proceed?`}
            </div>
            <div className="col col-6">
                <button className="btn btn-warning w-100" onClick={closeSlicerChangeModal}>
                    <span className="fs-5">
                        <RxCrossCircled />
                    </span> No
                </button>
            </div>
            <div className="col col-6">
                <button className="btn btn-success w-100" onClick={slicerChangeAction === "apply" ? applySelectedSlicers : resetSelectedSlicers}>
                    <span className="fs-5">
                        <IoIosCheckmarkCircleOutline />
                    </span> Yes
                </button>
            </div>
        </div>
    );

    return (
        <>
            <CheckboxDropdown
                id="promo-master-sku-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Product(s)"
                selectedValues={tempSelectedSlicers.skus.map((id: any) => id.toString())}
                items={slicers.skus}
                onItemChanged={(value: any) => dropdownItemChangeHandler("skus", value)}
            />

            <div className="d-inline me-1">
                <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: "#00833E" }} onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("apply") : applySelectedSlicers(e)
                } disabled={!isSlicerChanged} {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <IoChevronForwardCircleOutline size={20} /> &nbsp;Apply filters
                    </div>
                </button>
            </div>

            <div className="d-inline">
                <button type="button" className="btn btn-sm btn-warning" onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("reset") : resetSelectedSlicers(e)
                } {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <BiReset size={20} /> &nbsp;Reset filters
                    </div>
                </button>
            </div>

            {
                createPortal(
                    <Modal
                        id={slicerChangeModalId}
                        ref={modalCloseBtnRef}
                        title={`${(slicerChangeAction === "apply") ? "Apply" : "Reset"} Filters?`}
                        onModalClose={() => { }}
                    >
                        {slicerChangeModalBody}
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default PromoMasterSlicers;
